/**
 * PostHog ecommerce events (FRI-362)
 *
 * Explicit event capture layered on existing theme plumbing — no new UI hooks:
 * - theme.pubsub variantChange / cartUpdate / cartError (defined in theme.js)
 * - ajaxProduct:added DOM event (product-form ATC success)
 * - <cart-drawer> [open] attribute mutations (drawer views)
 * - button[name="checkout"] clicks (cart drawer + cart page)
 * - spa:navigated (SPA page transitions from spa-navigation.js)
 *
 * SSR context comes from snippets/posthog-events-config.liquid (window.__phContext).
 * Loaded with defer AFTER theme.js so theme.pubsub exists when this runs.
 *
 * add_to_cart is item-count-delta based: any cartUpdate that increases
 * cart.item_count counts as one add, regardless of source (product form,
 * bundle, upsell card, gift modal, drawer qty+). Filter on the `source`
 * property in funnels if you only want product-form adds. The error-path
 * cartUpdate republish in theme.js keeps the same count, so it never
 * produces a false add.
 *
 * A/B bucket registration: if localStorage `fri_ab_tests` holds a JSON
 * object (e.g. {"ab_aura_lp":"variant"}), each key is registered as a
 * super property on all subsequent events. Dormant until a test writes it.
 */
(function () {
  if (window.__phEventsLoaded) return;
  window.__phEventsLoaded = true;

  var ctx = window.__phContext || {};
  var lastItemCount = typeof ctx.cartItemCount === 'number' ? ctx.cartItemCount : null;
  var lastAddedVariantId = null;

  function ph(name, props) {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(name, props);
    }
  }

  // --- A/B test bucket → super properties (dormant until a test writes the key)
  try {
    var ab = JSON.parse(localStorage.getItem('fri_ab_tests') || 'null');
    if (ab && typeof ab === 'object' && !Array.isArray(ab) && window.posthog && window.posthog.register) {
      window.posthog.register(ab);
    }
  } catch (e) { /* corrupted key — ignore */ }

  // --- fresh A/B assignment made by the aura-lp gate (FRI-363). Two markers,
  // set pre-redirect in sessionStorage: friABPendingCapture = fire the
  // ab_assignment event here (only when the gate's pre-redirect beacon
  // couldn't run — brand-new visitors without a PostHog cookie yet);
  // friABPendingAttrs = stamp the cart attribute for order attribution
  // (always set on fresh assignment, whichever path captured the event).
  var na = null;
  try {
    na = JSON.parse(sessionStorage.getItem('friABPendingCapture') || 'null');
    if (na) sessionStorage.removeItem('friABPendingCapture');
  } catch (e) { /* ignore */ }
  if (na && na.test) {
    var reg = {};
    reg[na.test] = na.bucket;
    if (window.posthog && window.posthog.register) window.posthog.register(reg);
    ph('ab_assignment', { test: na.test, bucket: na.bucket });
  }
  var natt = null;
  try {
    natt = JSON.parse(sessionStorage.getItem('friABPendingAttrs') || 'null');
    if (natt) sessionStorage.removeItem('friABPendingAttrs');
  } catch (e) { /* ignore */ }
  if (natt && natt.test) {
    // Stamp the bucket on the cart so it lands on the order for attribution
    var attrs = {};
    attrs['_' + natt.test] = natt.bucket;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }

  // --- product_viewed (initial load + SPA navigations)
  function captureProductViewed(viaSpa) {
    if (viaSpa) {
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      if (!m) return;
      ph('product_viewed', { product_handle: m[1], spa_navigation: true });
      return;
    }
    if (ctx.pageType === 'product' && ctx.product) {
      ph('product_viewed', {
        product_id: ctx.product.id,
        product_handle: ctx.product.handle,
        product_title: ctx.product.title,
        product_type: ctx.product.type,
        price: ctx.product.price,
        variant_id: ctx.product.variantId,
        variant_title: ctx.product.variantTitle,
        template: ctx.template,
        spa_navigation: false
      });
    }
  }
  captureProductViewed(false);
  document.addEventListener('spa:navigated', function () { captureProductViewed(true); });

  // --- cart page counts as a cart view
  if (ctx.pageType === 'cart') {
    ph('cart_viewed', { cart_item_count: lastItemCount, cart_page: true });
  }

  // --- product-form ATC success detail (fallback enrichment for delta adds)
  document.addEventListener('ajaxProduct:added', function (e) {
    var p = e.detail && e.detail.product;
    if (p && p.variant_id) lastAddedVariantId = p.variant_id;
  });

  // --- checkout_started (drawer "Secure Checkout" + cart page button)
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"]');
    if (!btn) return;
    ph('checkout_started', { cart_item_count: lastItemCount });
  }, true);

  // --- pubsub wiring (variant + cart events)
  function wirePubsub() {
    if (!(window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS)) return false;

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, function (event) {
      var d = (event && event.data) || {};
      var v = d.variant || (d.data && d.data.variant);
      if (!v) return;
      ph('variant_selected', {
        variant_id: v.id,
        variant_title: v.title,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        price: typeof v.price === 'number' ? v.price / 100 : undefined,
        available: v.available,
        product_id: d.productId || (d.data && d.data.productId)
      });
    });

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
      var cart = event && event.cart;
      if (!cart || typeof cart.item_count !== 'number') return;
      var count = cart.item_count;
      if (lastItemCount === null) { lastItemCount = count; return; }
      var props = {
        source: event.source,
        cart_item_count: count,
        cart_total: typeof cart.total_price === 'number' ? cart.total_price / 100 : undefined,
        currency: cart.currency
      };
      if (count > lastItemCount) {
        props.items_added = count - lastItemCount;
        props.variant_id = event.productVariantId ? Number(event.productVariantId) : lastAddedVariantId;
        ph('add_to_cart', props);
      } else if (count < lastItemCount) {
        props.items_removed = lastItemCount - count;
        ph('cart_item_removed', props);
      }
      lastItemCount = count;
      lastAddedVariantId = null;
    });

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartError, function (event) {
      ph('add_to_cart_failed', {
        variant_id: event && event.productVariantId,
        message: event && event.message
      });
    });

    return true;
  }

  // --- cart drawer open → cart_viewed
  function wireCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var wasOpen = drawer.hasAttribute('open');
    new MutationObserver(function () {
      var isOpen = drawer.hasAttribute('open');
      if (isOpen && !wasOpen) {
        ph('cart_viewed', { cart_item_count: lastItemCount });
      }
      wasOpen = isOpen;
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // theme.js runs before this file (both defer, ordered), but guard against
  // load-order changes: retry briefly instead of failing silently.
  var attempts = 0;
  (function init() {
    var wired = wirePubsub();
    if (!wired && attempts++ < 50) return void setTimeout(init, 100);
    wireCartDrawer();
  })();
})();
